import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MessageSquare, Heart, Trash2, Send, ChevronDown, ChevronUp } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Comment({ c, onDelete, onLike, canDelete }) {
  return (
    <li className="border border-[color:var(--sat-border)] bg-[color:var(--sat-surface-2)] p-3"
        data-testid={`comment-${c.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="sat-label sat-cyan truncate" data-testid="comment-author">@{c.user_call_sign || "pilot"}</span>
          <span className="sat-label text-[color:var(--sat-text-dim)]">· {timeAgo(c.created_at)}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onLike(c)} data-testid={`comment-like-${c.id}`}
                  className={`flex items-center gap-1 px-1.5 py-1 text-[11px] font-mono border transition-colors ${
                    c.i_liked
                      ? "border-[color:var(--sat-cyan)] sat-cyan"
                      : "border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] hover:sat-cyan hover:border-[color:var(--sat-cyan)]"
                  }`}
                  aria-label={c.i_liked ? "Unlike" : "Like"}>
            <Heart size={11} fill={c.i_liked ? "currentColor" : "none"} strokeWidth={1.5} />
            <span className="tabular-nums" data-testid={`comment-likes-${c.id}`}>{c.likes_count}</span>
          </button>
          {canDelete && (
            <button onClick={() => onDelete(c)}
                    data-testid={`comment-delete-${c.id}`}
                    className="p-1.5 border border-[color:var(--sat-border)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-live)] hover:border-[color:var(--sat-live)]"
                    aria-label="Delete comment">
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-[color:var(--sat-text)] whitespace-pre-wrap break-words" data-testid="comment-body">
        {c.body}
      </p>
    </li>
  );
}

/**
 * CommentsSection — a threaded (flat) discussion panel for a target entity.
 * Props:
 *   - targetKind: "news" | "launch" | "rocket" | "milestone"
 *   - targetId:   string
 *   - initiallyOpen: bool (default true; NewsPage uses false for collapse UX)
 *   - variant:    "block" (default, always-open) | "collapsible" (chevron toggle)
 *   - testid:     optional root data-testid
 */
export default function CommentsSection({ targetKind, targetId, initiallyOpen = true, variant = "block", testid }) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [open, setOpen] = useState(initiallyOpen);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const load = useCallback(async () => {
    if (!open) {
      // Just fetch count for the collapsed badge
      try {
        const r = await api.get(`/comments/count`, { params: { target_kind: targetKind, target_id: targetId } });
        setCount(r.data?.count ?? 0);
      } catch (_) {}
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/comments`, { params: { target_kind: targetKind, target_id: targetId } });
      const list = r.data?.results || [];
      setItems(list);
      setCount(list.length);
    } catch (_) {
      setItems([]); setCount(0);
    } finally {
      setLoading(false);
    }
  }, [open, targetKind, targetId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("PILOT AUTHENTICATION REQUIRED"); return; }
    const trimmed = body.trim();
    if (!trimmed) return;
    setPosting(true);
    try {
      const r = await api.post("/comments", { target_kind: targetKind, target_id: targetId, body: trimmed });
      setItems(prev => [...prev, r.data]);
      setCount(c => c + 1);
      setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Comment failed");
    } finally { setPosting(false); }
  };

  const onDelete = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${c.id}`);
      setItems(prev => prev.filter(x => x.id !== c.id));
      setCount(c => Math.max(0, c - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onLike = async (c) => {
    if (!user) { toast.error("PILOT AUTHENTICATION REQUIRED"); return; }
    try {
      const r = await api.post(`/comments/${c.id}/like`);
      setItems(prev => prev.map(x => x.id === c.id ? r.data : x));
    } catch (_) {}
  };

  const canDelete = (c) => user && (user.id === c.user_id || isAdmin);
  const collapsible = variant === "collapsible";

  return (
    <section
      className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4 md:p-5"
      data-testid={testid || `comments-${targetKind}-${targetId}`}
    >
      <div
        className={`flex items-center justify-between gap-2 ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <div className="flex items-center gap-2">
          <MessageSquare size={13} className="sat-cyan" />
          <span className="sat-label sat-cyan">COMMS // DISCUSSION</span>
          <span className="sat-label text-[color:var(--sat-text-dim)]" data-testid="comments-count">
            {count} {count === 1 ? "MESSAGE" : "MESSAGES"}
          </span>
        </div>
        {collapsible && (open ? <ChevronUp size={13} className="text-[color:var(--sat-text-dim)]" />
                              : <ChevronDown size={13} className="text-[color:var(--sat-text-dim)]" />)}
      </div>

      {open && (
        <>
          {loading ? (
            <div className="sat-label mt-3 text-[color:var(--sat-text-dim)]">RECEIVING TRANSMISSIONS…</div>
          ) : items.length === 0 ? (
            <div className="mt-4 pt-4 border-t border-[color:var(--sat-border)] text-sm text-[color:var(--sat-text-dim)]"
                 data-testid="comments-empty">
              NO TRANSMISSIONS YET — BE THE FIRST TO WEIGH IN.
            </div>
          ) : (
            <ul className="mt-4 pt-4 border-t border-[color:var(--sat-border)] space-y-2" data-testid="comments-list">
              {items.map(c => (
                <Comment key={c.id} c={c} onLike={onLike} onDelete={onDelete} canDelete={canDelete(c)} />
              ))}
            </ul>
          )}

          {/* Composer */}
          <div className="mt-4 pt-4 border-t border-[color:var(--sat-border)]">
            {user ? (
              <form onSubmit={submit} data-testid="comment-form">
                <div className="sat-label mb-1.5 text-[color:var(--sat-text-dim)]">
                  BROADCASTING AS <span className="sat-cyan">@{user.call_sign || (user.email || "").split("@")[0]}</span>
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="TRANSMIT MESSAGE…"
                  className="w-full"
                  data-testid="comment-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="sat-label text-[color:var(--sat-text-dim)] tabular-nums">
                    {body.length}/2000
                  </span>
                  <button type="submit" disabled={posting || !body.trim()}
                          data-testid="comment-submit"
                          className="sat-btn sat-btn-solid disabled:opacity-50">
                    <Send size={12} /> {posting ? "SENDING…" : "TRANSMIT"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-sm text-[color:var(--sat-text-dim)]" data-testid="comment-auth-prompt">
                <Link to="/auth" className="sat-cyan hover:underline">AUTHENTICATE</Link> to join the discussion.
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
